// ============================================================================
// EcgAnalysisPage.tsx — ПРИЛОЖЕНИЕ «ЭКГ»: экран врача.
//
// Две вкладки:
//   • «Очередь исследований» (по умолчанию для врача/преподавателя) — записи
//     ЭКГ: дата, пациент, статус, поиск/фильтры, карточка исследования,
//     заключение врача и история пациента;
//   • «Новый разбор» — снимок уходит на СЕРВЕР платформы
//     (EcgService.AnalyzeEcg): сервер вызывает сервис-анализатор по своему
//     адресу. Клиент НЕ обращается к localhost — на телефоне это был бы сам
//     телефон, а по https браузер блокировал бы http. Умная съёмка
//     (canvas-оценка кадра ДО отправки), сохранение разбора в справочник
//     cardio_studies (+ ТЧ «Находки») и офлайн-очередь.
//
// Файл выбирается через label-обёртку над input type=file: в WebView2
// программный click по input вне DOM блокируется (как в FileListTab).
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Activity,
    Camera,
    ClipboardList,
    CloudOff,
    RefreshCw,
    Save,
    Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TabBar } from '@/components/ui/TabBar';
import { CameraCaptureDialog } from '@/components/media/CameraCaptureDialog';
import { useToastStore } from '@/store/toastStore';
import { useAuthStore } from '@/store/authStore';
import { getSDK } from '@/store/sdkStore';
import { friendlyError, isTransportError, logErrorToJournal } from '@/lib/errors';
import { isEffectivelyOffline, subscribeOfflineFlag } from '@/lib/offlineFlag';
import { analyzeEcgImage, type EcgCaptureAssessment } from '@/lib/ecgImageQuality';
import { analyzeEcgFile, describeAnalyzeError, ecgFileIssue, isAnalyzeTimeout, type EcgAnalysis } from '@/lib/ecgAnalysis';
import { buildQualityLine, type EcgStudyFinding } from '@/lib/ecgStudies';
import { queueEcgCapture, saveEcgStudy } from '@/lib/ecgOfflineQueue';
import { EcgCaptureQualityPanel } from '@/components/ecg/EcgCaptureQualityPanel';
import { EcgResultView } from '@/components/ecg/EcgResultView';
import { EcgStudiesPanel } from '@/components/ecg/EcgStudiesPanel';
import { EcgStudyDetailDialog } from '@/components/ecg/EcgStudyDetailDialog';
import { EcgPatientHistoryDialog } from '@/components/ecg/EcgPatientHistoryDialog';
import { EcgOfflineQueuePanel } from '@/components/ecg/EcgOfflineQueuePanel';
import { useSecurityAccess } from '@/lib/securityRoles';
import {
    ECG_DOCTOR_ROLE,
    ECG_TEACHER_ROLE,
} from '@/lib/ecgRoles';
import type { EcgPatient } from '@/lib/ecgPatients';

/** Код приложения «ЭКГ-разбор» (рендерер регистрируется клиентом). */
export const ECG_APP_CODE = 'ecg';

/** Результаты разбора описаны в lib/ecgAnalysis (SSoT для страницы и очереди). */
export type { EcgAnalysis, EcgFinding, EcgMeasurements, EcgQuality } from '@/lib/ecgAnalysis';

type EcgTab = 'queue' | 'analyze';

/** Состояние серверного анализатора (диагностика на странице). */
interface EcgAnalyzerState {
    available: boolean;
    version: string;
    detail: string;
}

function userIdOf(user: unknown): string {
    if (typeof user !== 'object' || user === null || !('id' in user)) return '';
    const id = (user as { id?: unknown }).id;
    if (typeof id === 'string') return id;
    if (typeof id === 'number') return String(id);
    return '';
}

export function EcgAnalysisPage() {
    const showError = useToastStore((state) => state.showError);
    const showSuccess = useToastStore((state) => state.showSuccess);
    const myId = useAuthStore((state) => userIdOf(state.user));
    const access = useSecurityAccess();
    const isDoctor = access.roles.includes(ECG_DOCTOR_ROLE) || access.roles.includes(ECG_TEACHER_ROLE);

    const [tab, setTab] = useState<EcgTab>(isDoctor ? 'queue' : 'analyze');
    const [activeStudyId, setActiveStudyId] = useState('');
    const [patient, setPatient] = useState<EcgPatient | null>(null);
    const [studiesRefreshToken, setStudiesRefreshToken] = useState(0);

    const [analyzer, setAnalyzer] = useState<EcgAnalyzerState | null>(null);
    const [analyzerError, setAnalyzerError] = useState('');
    const [analyzerChecking, setAnalyzerChecking] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [fileSource, setFileSource] = useState<'camera' | 'file' | null>(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [result, setResult] = useState<EcgAnalysis | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [analyzedAt, setAnalyzedAt] = useState('');
    /** Связь недоступна: снимок сохраняется в durable-очередь (разбор — при связи). */
    const [offline, setOffline] = useState(isEffectivelyOffline());
    /** Снимок этой сессии, ожидающий разбора в очереди. */
    const [queuedCaptureId, setQueuedCaptureId] = useState('');
    const [queueing, setQueueing] = useState(false);
    /** Итог автоматического разбора отложенного снимка (явное состояние). */
    const [queueResult, setQueueResult] = useState('');

    useEffect(() => subscribeOfflineFlag(() => setOffline(isEffectivelyOffline())), []);

    const [capture, setCapture] = useState<EcgCaptureAssessment | null>(null);
    const [captureLoading, setCaptureLoading] = useState(false);
    const [captureError, setCaptureError] = useState('');

    const [saving, setSaving] = useState(false);
    const [savedStudyId, setSavedStudyId] = useState('');
    const [savedQueued, setSavedQueued] = useState(false);

    /**
     * Проверка доступности анализатора (диагностика на странице).
     * Доступна и по кнопке: после временного сбоя пользователь видит
     * актуальное состояние, а не «застывшее» с момента открытия.
     */
    const analyzerRequestRef = useRef(0);
    const checkAnalyzer = useCallback(async () => {
        const request = ++analyzerRequestRef.current;
        setAnalyzerChecking(true);
        try {
            const status = await getSDK().ecg.getEcgAnalyzerStatus({});
            if (request !== analyzerRequestRef.current) return;
            setAnalyzer({
                available: status.available,
                version: status.version,
                detail: status.detail,
            });
            setAnalyzerError('');
        } catch (statusError) {
            if (request !== analyzerRequestRef.current) return;
            setAnalyzer(null);
            setAnalyzerError(friendlyError(statusError));
            logErrorToJournal(statusError, 'ЭКГ: проверка анализатора');
        } finally {
            if (request === analyzerRequestRef.current) setAnalyzerChecking(false);
        }
    }, []);

    useEffect(() => {
        void checkAnalyzer();
    }, [checkAnalyzer]);

    useEffect(() => {
        if (!file) {
            setCapture(null);
            setCaptureError('');
            setCaptureLoading(false);
            return;
        }
        // Недопустимый файл (пустой/больше 25 МБ/не изображение) оценивать
        // бессмысленно: ошибку показывает страница, отправки не будет.
        if (ecgFileIssue(file)) {
            setCapture(null);
            setCaptureError('');
            setCaptureLoading(false);
            return;
        }
        let cancelled = false;
        setCapture(null);
        setCaptureError('');
        setCaptureLoading(true);
        analyzeEcgImage(file)
            .then((assessment) => {
                if (!cancelled) setCapture(assessment);
            })
            .catch((assessmentError: unknown) => {
                if (cancelled) return;
                setCaptureError(friendlyError(assessmentError));
                logErrorToJournal(assessmentError, 'ЭКГ: оценка качества снимка');
            })
            .finally(() => {
                if (!cancelled) setCaptureLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [file]);

    const applyFile = useCallback((picked: File | null, source: 'camera' | 'file' | null) => {
        setFile(picked);
        setFileSource(picked ? source : null);
        // Проверка файла до сети: ошибка видна сразу после выбора.
        setError(picked ? ecgFileIssue(picked) : '');
        setResult(null);
        setAnalyzedAt('');
        setSavedStudyId('');
        setSavedQueued(false);
        setQueuedCaptureId('');
        setQueueResult('');
    }, []);

    const pickFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const picked = event.target.files && event.target.files.length > 0 ? event.target.files[0] : null;
        applyFile(picked, 'file');
    };

    const retake = useCallback(() => {
        applyFile(null, null);
        if (fileSource === 'camera') setCameraOpen(true);
    }, [applyFile, fileSource]);

    /**
     * queueCapture — положить снимок в durable-очередь (нет связи/сбой сети).
     * Разбор и сохранение выполнит очередь при восстановлении связи (или по
     * кнопке «Отправить сейчас» в панели отложенных разборов).
     */
    const queueCapture = useCallback(async (reason: string) => {
        if (!file) return;
        const issue = ecgFileIssue(file);
        if (issue) {
            setError(issue);
            showError(issue);
            return;
        }
        if (!myId) {
            const message = 'Войдите, чтобы сохранить снимок в очередь';
            setError(message);
            showError(message);
            return;
        }
        setQueueing(true);
        try {
            const item = await queueEcgCapture({
                ownerId: myId,
                file,
                sharpnessScore: capture ? capture.sharpnessScore : null,
                reason,
            });
            setQueuedCaptureId(item.id);
            setQueueResult('');
            setError('');
            setResult(null);
            setAnalyzedAt('');
            setSavedStudyId('');
            setSavedQueued(false);
            showSuccess('Нет связи — снимок сохранён. Разбор и сохранение выполнятся автоматически при подключении.');
        } catch (queueError) {
            const message = friendlyError(queueError);
            logErrorToJournal(queueError, 'ЭКГ: сохранение снимка в очередь');
            setError(message);
            showError(message);
        } finally {
            setQueueing(false);
        }
    }, [file, myId, capture, showError, showSuccess]);

    const analyze = useCallback(async () => {
        if (!file) {
            setError('Сначала выберите изображение ЭКГ');
            return;
        }
        const issue = ecgFileIssue(file);
        if (issue) {
            // Тот же предел, что на сервере: явная ошибка до отправки.
            setError(issue);
            showError(issue);
            return;
        }
        if (queuedCaptureId) {
            // Снимок этой сессии уже в очереди — не создаём дубль.
            showError('Этот снимок уже в очереди — дождитесь связи или отмените его в «Отложенных разборах».');
            return;
        }
        setError('');
        setResult(null);
        setSavedStudyId('');
        setSavedQueued(false);
        setQueuedCaptureId('');
        // Нет связи — сразу в durable-очередь: снимок не теряется и будет
        // разобран тем же серверным механизмом при восстановлении связи.
        if (isEffectivelyOffline()) {
            await queueCapture('Нет связи — снимок ожидает разбора');
            return;
        }
        setLoading(true);
        try {
            const analysis = await analyzeEcgFile(file);
            setResult(analysis);
            setAnalyzedAt(new Date().toLocaleTimeString('ru-RU'));
        } catch (analyzeError) {
            // Таймаут — это НЕ потеря связи: запрос мог дойти до сервера.
            // Показываем явную ошибку и НЕ прячем её в офлайн-очередь.
            if (isAnalyzeTimeout(analyzeError)) {
                const message = describeAnalyzeError(analyzeError);
                logErrorToJournal(analyzeError, 'ЭКГ: анализ снимка — сервер не ответил');
                setError(message);
                showError(message);
                return;
            }
            if (isTransportError(analyzeError) || isEffectivelyOffline()) {
                logErrorToJournal(analyzeError, 'ЭКГ: анализ снимка — связь потеряна');
                await queueCapture(describeAnalyzeError(analyzeError));
                return;
            }
            const message = describeAnalyzeError(analyzeError);
            logErrorToJournal(analyzeError, 'ЭКГ: анализ снимка');
            setError(message);
            showError(message);
        } finally {
            setLoading(false);
        }
    }, [file, queuedCaptureId, queueCapture, showError]);

    const saveStudy = useCallback(async () => {
        if (!result || !file || !myId) return;
        if (result.status !== 'ok' && result.status !== 'retake') return;
        setSaving(true);
        setError('');
        try {
            const findings: EcgStudyFinding[] = result.findings.map((finding) => ({
                code: finding.code,
                probability: finding.probability,
            }));
            const saved = await saveEcgStudy({
                ownerId: myId,
                file,
                status: result.status === 'ok' ? 'processed' : 'retake',
                qualityLine: buildQualityLine(
                    result.quality.verdict,
                    capture ? capture.sharpnessScore : null,
                    result.quality.leadsExtracted,
                ),
                reason: result.reason,
                pipelineVersion: result.pipelineVersion,
                measurements: result.measurements,
                findings,
            });
            if (saved.queued) {
                setSavedQueued(true);
                setSavedStudyId(saved.queueId || 'queued');
                showSuccess('Нет связи — разбор сохранён в очередь и отправится при подключении');
                return;
            }
            setSavedQueued(false);
            setSavedStudyId(saved.studyId);
            setStudiesRefreshToken((token) => token + 1);
            if (saved.findingsFailed.length > 0) {
                for (const failure of saved.findingsFailed) {
                    logErrorToJournal(failure.error, `ЭКГ: сохранение находки «${failure.code}»`);
                }
                const codes = saved.findingsFailed.map((failure) => failure.code).join(', ');
                showError(`Разбор сохранён, но находки записаны не все: ${codes}`);
            } else {
                showSuccess('Разбор сохранён');
            }
        } catch (saveError) {
            const message = friendlyError(saveError);
            logErrorToJournal(saveError, 'ЭКГ: сохранение разбора');
            setError(message);
            showError(message);
        } finally {
            setSaving(false);
        }
    }, [result, file, myId, capture, showError, showSuccess]);

    const openStudyFromAnalysis = () => {
        if (!savedStudyId || savedQueued) return;
        setActiveStudyId(savedStudyId);
    };

    const openPatientHistory = (next: EcgPatient) => {
        setActiveStudyId('');
        setPatient(next);
    };

    const canSave = !!result && (result.status === 'ok' || result.status === 'retake');

    // Стабильные колбэки очереди: панель подписывается на изменения и не
    // должна переподписываться на каждый рендер страницы.
    const handleQueueSynced = useCallback((savedIds: string[]) => {
        setStudiesRefreshToken((token) => token + 1);
        if (savedQueued && savedIds.includes(savedStudyId)) setSavedQueued(false);
        if (queuedCaptureId && savedIds.includes(queuedCaptureId)) {
            setQueuedCaptureId('');
            setQueueResult('Отложенный снимок разобран и сохранён — результат в «Очереди исследований».');
            showSuccess('Отложенный снимок разобран и сохранён');
        }
    }, [savedQueued, savedStudyId, queuedCaptureId, showSuccess]);

    const handleQueueCanceled = useCallback((id: string) => {
        if (id !== queuedCaptureId) return;
        setQueuedCaptureId('');
        setQueueResult('');
    }, [queuedCaptureId]);

    const tabs = [
        {
            id: 'queue',
            label: (<><ClipboardList className="h-3 w-3 inline mr-1" />Очередь исследований</>),
        },
        {
            id: 'analyze',
            label: (<><Camera className="h-3 w-3 inline mr-1" />Новый разбор</>),
        },
    ];

    return (
        <div className="h-full flex flex-col min-h-0">
            <div className="px-4 py-2.5 border-b flex items-center gap-2 shrink-0 flex-wrap">
                <Activity className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">ЭКГ</span>
                <span className="text-xs text-muted-foreground hidden sm:inline">вероятностный анализ</span>
                <div className="ml-auto">
                    <TabBar tabs={tabs} active={tab} onChange={(id) => setTab(id as EcgTab)} group="ecg" />
                </div>
            </div>

            {tab === 'queue' ? (
                <div className="flex-1 min-h-0 p-4">
                    <EcgStudiesPanel
                        refreshToken={studiesRefreshToken}
                        onOpenPatient={setPatient}
                        onOpenStudy={setActiveStudyId}
                    />
                </div>
            ) : (
                <div className="flex-1 overflow-auto p-4 space-y-4">
                    <section className="border rounded-lg p-3 bg-card space-y-3">
                        <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs">
                            <div className="flex flex-wrap items-center gap-2">
                                <span
                                    className={`inline-block h-2 w-2 rounded-full ${
                                        analyzer?.available
                                            ? 'bg-emerald-500'
                                            : analyzerError
                                                ? 'bg-destructive'
                                                : 'bg-amber-500'
                                    }`}
                                />
                                <span className="font-medium">Разбор выполняет сервер платформы</span>
                                {analyzer?.available && analyzer.version && (
                                    <span className="text-muted-foreground">· анализатор {analyzer.version}</span>
                                )}
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 px-2 text-[11px] ml-auto"
                                    onClick={() => void checkAnalyzer()}
                                    disabled={analyzerChecking || loading}
                                    title="Проверить доступность сервиса-анализатора"
                                >
                                    <RefreshCw className={`h-3 w-3 ${analyzerChecking ? 'animate-spin' : ''}`} />
                                    Проверить
                                </Button>
                            </div>
                            <div className="mt-1 text-muted-foreground">
                                {analyzerError
                                    ? analyzerError
                                    : analyzer?.available
                                        ? 'Сервис-анализатор доступен.'
                                        : (analyzer?.detail || 'Проверяю доступность сервиса-анализатора…')}
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <label className="inline-flex items-center gap-2 h-8 px-3 rounded-md border bg-background text-sm cursor-pointer hover:bg-accent/40">
                                <Upload className="h-4 w-4" />
                                <span>{file ? file.name : 'Выбрать изображение ЭКГ'}</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onClick={(event) => { event.currentTarget.value = ''; }}
                                    onChange={pickFile}
                                />
                            </label>

                            <Button
                                size="sm"
                                variant="outline"
                                className="h-8"
                                onClick={() => setCameraOpen(true)}
                                disabled={loading}
                            >
                                <Camera className="h-4 w-4" />
                                Сфотографировать ЭКГ
                            </Button>

                            <Button
                                size="sm"
                                className="h-8"
                                onClick={() => void analyze()}
                                disabled={loading || queueing || !file}
                            >
                                {loading || queueing
                                    ? <RefreshCw className="h-4 w-4 animate-spin" />
                                    : offline
                                        ? <CloudOff className="h-4 w-4" />
                                        : <Activity className="h-4 w-4" />}
                                {loading
                                    ? 'Разбираю…'
                                    : queueing
                                        ? 'Сохраняю…'
                                        : offline
                                            ? 'Сохранить в очередь'
                                            : 'Разобрать'}
                            </Button>

                            {analyzedAt && <span className="text-xs text-muted-foreground">Разобрано в {analyzedAt}</span>}
                        </div>

                        {file && (
                            <EcgCaptureQualityPanel
                                assessment={capture}
                                loading={captureLoading}
                                error={captureError}
                                onRetake={retake}
                                onSendAnyway={() => void analyze()}
                            />
                        )}
                    </section>

                    {(offline || queuedCaptureId) && (
                        <div className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs space-y-1">
                            {offline && (
                                <div className="flex items-start gap-2">
                                    <CloudOff className="h-3.5 w-3.5 mt-0.5 text-amber-600 shrink-0" />
                                    <span>
                                        Нет связи: офлайн разбор не выполняется. Снимок сохранится на
                                        устройстве (durable-очередь) и будет разобран автоматически при
                                        восстановлении связи — перезапуск приложения его не потеряет.
                                    </span>
                                </div>
                            )}
                            {queuedCaptureId && (
                                <div className="text-amber-900">
                                    Снимок в очереди: ожидает разбора и сохранения. Повторить или
                                    отменить — в «Отложенных разборах» ниже.
                                </div>
                            )}
                        </div>
                    )}

                    {error && (
                        <div className="text-sm text-destructive border border-destructive/40 rounded-md px-3 py-2 flex items-center gap-2">
                            <span className="flex-1">{error}</span>
                            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => void analyze()} disabled={loading || !file}>
                                Повторить
                            </Button>
                        </div>
                    )}

                    {loading && (
                        <div className="text-sm text-muted-foreground">Отправляю снимок в сервис и жду результат…</div>
                    )}

                    {queueResult && (
                        <div className="text-sm text-emerald-700 border border-emerald-500/40 bg-emerald-500/10 rounded-md px-3 py-2">
                            {queueResult}
                        </div>
                    )}

                    {result && <EcgResultView result={result} />}

                    {canSave && (
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                size="sm"
                                className="h-8"
                                onClick={() => void saveStudy()}
                                disabled={saving || !myId || !!savedStudyId}
                            >
                                {saving
                                    ? <RefreshCw className="h-4 w-4 animate-spin" />
                                    : <Save className="h-4 w-4" />}
                                {saving
                                    ? 'Сохраняю…'
                                    : savedStudyId
                                        ? (savedQueued ? 'Разбор в очереди' : 'Разбор сохранён')
                                        : 'Сохранить разбор'}
                            </Button>
                            {!myId && <span className="text-xs text-muted-foreground">Войдите, чтобы сохранить разбор.</span>}
                            {savedStudyId && !savedQueued && (
                                <Button size="sm" variant="outline" className="h-8" onClick={openStudyFromAnalysis}>
                                    Открыть карточку
                                </Button>
                            )}
                        </div>
                    )}

                    <EcgOfflineQueuePanel
                        onSynced={handleQueueSynced}
                        onCanceled={handleQueueCanceled}
                    />

                    {!result && !loading && !error && (
                        <div className="text-sm text-muted-foreground">
                            Выберите снимок ЭКГ и нажмите «Разобрать».
                        </div>
                    )}
                </div>
            )}

            <EcgStudyDetailDialog
                studyId={activeStudyId}
                onClose={() => setActiveStudyId('')}
                onOpenPatient={openPatientHistory}
                onSaved={() => setStudiesRefreshToken((token) => token + 1)}
            />

            <EcgPatientHistoryDialog
                patient={patient}
                onClose={() => setPatient(null)}
                onOpenStudy={(studyId) => {
                    setPatient(null);
                    setActiveStudyId(studyId);
                }}
            />

            <CameraCaptureDialog
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCaptured={({ file: shot }) => {
                    setCameraOpen(false);
                    applyFile(shot, 'camera');
                }}
                fileNamePrefix="ЭКГ"
                jpegQuality={0.95}
                maxWidth={2560}
                allowLocation={false}
                initialFacingMode="environment"
            />
        </div>
    );
}
